import {
	ExtractionStatus,
	FileType,
	FinancialPeriodPreparationMethod,
	isWipExtractableExtension,
	PeriodType,
	type WipSchedule,
} from "@inrev/common";
import { type AttachmentActionsConfig } from "@portal/api/attachments";
import type { Row, SortingState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { DynamicDataTableColumn } from "../ui/DynamicDataTable/types";
import { ComponentCell } from "./ComponentCell";
import { ExtractionButton } from "./ExtractionButton";
import { ExtractionStatusIcon } from "./ExtractionStatusIcon";
import { UploadedDocumentRow } from "./types";
import { UploadedDocumentNameWithIcon } from "./UploadedDocumentNameCell";
import {
	UploadedDocumentRowActionsCell,
	type UploadedDocumentRowActionsConfig,
} from "./UploadedDocumentRowActions";
import { UploadedDocumentsTagsCell } from "./UploadedDocumentsTagsCell";

export const UPLOADED_DOCUMENTS_INITIAL_SORTING: SortingState = [{ id: "uploadedAt", desc: true }];

export type UploadedDocumentsColumnsActionsConfig =
	UploadedDocumentRowActionsConfig<UploadedDocumentRow> & {
		updateAttachmentTypes: AttachmentActionsConfig["updateAttachmentTypes"];
		allowTypeConfirmation: AttachmentActionsConfig["allowTypeConfirmation"];
	};

export function createUploadedDocumentsColumns({
	typeLabelMap,
	actions,
}: {
	typeLabelMap?: Partial<Record<FileType, string>>;
	actions: UploadedDocumentsColumnsActionsConfig;
}): DynamicDataTableColumn<UploadedDocumentRow>[] {
	return [
		{
			id: "actions",
			header: "Actions",
			size: 80,
			enableSorting: false,
			cell: ({ row }) => (
				<div className="justify-center min-w-[47px]">
					<UploadedDocumentRowActionsCell row={row.original} {...actions} />
				</div>
			),
		},
		{
			id: "documentName",
			header: "Document Name",
			size: 350,
			minSize: 200,
			enableResizing: true,
			enableSorting: false,
			cell: ({ row }) => <UploadedDocumentNameWithIcon actions={actions} row={row.original} />,
		},
		{
			id: "uploadedAt",
			accessorKey: "createdAtMs",
			header: "Uploaded",
			size: 110,
			sortDescFirst: true,
			enableSorting: true,
			cell: ({ row }) => (
				<span className="text-xs xl:text-sm">
					{DateTime.fromMillis(row.original.createdAtMs).toFormat("MM/dd/yyyy")}
				</span>
			),
		},
		{
			id: "tags",
			header: "Tags",
			size: 460,
			minSize: 460,
			enableSorting: false,
			enableResizing: true,
			cell: ({ row }) => (
				<UploadedDocumentsTagsCell
					row={row.original}
					updateAttachmentTypes={actions.updateAttachmentTypes}
					typeLabelMap={typeLabelMap}
					allowTypeConfirmation={actions.allowTypeConfirmation}
				/>
			),
		},
	];
}

type RetryConfig = {
	onTriggerExtraction: (row: UploadedDocumentRow) => void;
	extractionPending: boolean;
};

export function createFinancialsDocumentsColumns(
	actions: UploadedDocumentRowActionsConfig<UploadedDocumentRow>,
	isAdmin: boolean,
	retryConfig?: RetryConfig,
): DynamicDataTableColumn<UploadedDocumentRow>[] {
	const ACTION_BUTTON_WIDTH = 10;
	const BASE_ACTION_COLUMN_SIZE = 80;
	const actionColumnSize = BASE_ACTION_COLUMN_SIZE + (isAdmin ? ACTION_BUTTON_WIDTH : 0);
	return [
		{
			id: "action_menu",
			header: "Actions",
			size: actionColumnSize,
			enableSorting: false,
			cell: ({ row }) => (
				<div className="justify-center">
					<UploadedDocumentRowActionsCell
						row={row.original}
						{...actions}
						leadingSlot={
							isAdmin && retryConfig ? (
								<ExtractionButton
									row={row.original}
									onTrigger={retryConfig.onTriggerExtraction}
									extractionStatus={row.original.financialExtraction?.status}
									triggerPending={retryConfig.extractionPending}
									hasExistingExtraction={!!row.original.financialExtraction}
								/>
							) : undefined
						}
					/>
				</div>
			),
		},
		{
			id: "documentName",
			header: "Document Name",
			size: 220,
			minSize: 200,
			enableSorting: true,
			enableResizing: true,
			cell: ({ row }) => <UploadedDocumentNameWithIcon actions={actions} row={row.original} />,
		},
		{
			id: "periodEndDate",
			accessorFn: ({ financialExtraction }) =>
				financialExtraction?.periodEndDate
					? DateTime.fromISO(financialExtraction.periodEndDate)
					: undefined,
			header: "Period End",
			size: 110,
			sortingFn: "datetime",
			enableSorting: true,
			cell: ({ row }) => {
				const periodEndDate = row.original.financialExtraction?.periodEndDate;
				if (!periodEndDate) {
					return <span className="text-tertiary">—</span>;
				}
				const dt = DateTime.fromISO(periodEndDate);
				return (
					<span className="text-secondary text-xs xl:text-sm">
						{dt.isValid ? dt.toFormat("MM/dd/yyyy") : "—"}
					</span>
				);
			},
		},
		{
			id: "periodType",
			accessorFn: ({ financialExtraction }) =>
				financialExtraction?.periodType
					? PeriodType.displayName(financialExtraction.periodType)
					: "",
			header: "Type",
			size: 80,
			headerFilter: { type: "checkbox" },
			cell: ({ row }) => {
				const periodType = row.original.financialExtraction?.periodType;
				if (!periodType) {
					return <span className="text-tertiary">—</span>;
				}
				return <span className="text-secondary text-sm">{PeriodType.displayName(periodType)}</span>;
			},
		},
		{
			id: "preparationMethod",
			accessorFn: ({ financialExtraction }) =>
				financialExtraction?.preparationMethod
					? FinancialPeriodPreparationMethod.displayName(financialExtraction.preparationMethod)
					: "",
			header: "Preparation",
			size: 115,
			headerFilter: { type: "checkbox" },
			cell: ({ row }) => {
				const preparationMethod = row.original.financialExtraction?.preparationMethod;
				if (!preparationMethod) {
					return <span className="text-tertiary">—</span>;
				}
				return (
					<span className="text-secondary text-sm">
						{FinancialPeriodPreparationMethod.displayName(preparationMethod)}
					</span>
				);
			},
		},
		...(isAdmin
			? [
					{
						id: "components",
						header: "Components",
						size: 180,
						enableSorting: false,
						cellStyle: { centerItems: true },
						cell: ({ row }: { row: Row<UploadedDocumentRow> }) => (
							<ComponentCell row={row.original} />
						),
					},
				]
			: []),
		{
			id: "uploadedAt",
			accessorKey: "createdAtMs",
			header: "Uploaded",
			size: 110,
			sortDescFirst: true,
			enableSorting: true,
			cell: ({ row }) => (
				<span className="text-secondary text-xs xl:text-sm">
					{DateTime.fromMillis(row.original.createdAtMs).toFormat("MM/dd/yyyy")}
				</span>
			),
		},
		{
			id: "extractionStatus",
			accessorFn: ({ financialExtraction }) =>
				financialExtraction?.status ? ExtractionStatus.displayName(financialExtraction.status) : "",
			header: "Status",
			size: 100,
			headerFilter: { type: "checkbox" },
			cellStyle: { centerItems: true },
			cell: ({ row }) => (
				<ExtractionStatusIcon
					status={row.original.financialExtraction?.status}
					errorType={row.original.financialExtraction?.errorType}
					errorMessage={row.original.financialExtraction?.errorMessage}
					extractionTriggeredAt={
						row.original.financialExtraction?.extractionTriggeredAt
							? DateTime.fromISO(row.original.financialExtraction.extractionTriggeredAt)
							: undefined
					}
					extractionCompletedAt={
						row.original.financialExtraction?.extractionCompletedAt
							? DateTime.fromISO(row.original.financialExtraction.extractionCompletedAt)
							: undefined
					}
				/>
			),
		},
	];
}

const WIP_CONFIDENCE_LABELS: Record<WipSchedule.WipConfidenceTier, string> = {
	VERIFIED: "Verified",
	PARTIAL: "Partial",
	UNCERTAIN: "Uncertain",
};

export function createWipDocumentsColumns(
	actions: UploadedDocumentRowActionsConfig<UploadedDocumentRow>,
	isAdmin: boolean,
	retryConfig?: RetryConfig,
): DynamicDataTableColumn<UploadedDocumentRow>[] {
	const ACTION_BUTTON_WIDTH = 10;
	const BASE_ACTION_COLUMN_SIZE = 80;
	const actionColumnSize = BASE_ACTION_COLUMN_SIZE + (isAdmin ? ACTION_BUTTON_WIDTH : 0);
	return [
		{
			id: "action_menu",
			header: "Actions",
			size: actionColumnSize,
			enableSorting: false,
			cell: ({ row }) => (
				<div className="justify-center">
					<UploadedDocumentRowActionsCell
						row={row.original}
						{...actions}
						leadingSlot={
							isAdmin && retryConfig ? (
								<ExtractionButton
									row={row.original}
									onTrigger={retryConfig.onTriggerExtraction}
									extractionStatus={row.original.wipExtraction?.status}
									triggerPending={retryConfig.extractionPending}
									hasExistingExtraction={!!row.original.wipExtraction}
									isExtensionSupported={isWipExtractableExtension}
								/>
							) : undefined
						}
					/>
				</div>
			),
		},
		{
			id: "documentName",
			header: "Document Name",
			size: 220,
			minSize: 200,
			enableSorting: true,
			enableResizing: true,
			cell: ({ row }) => <UploadedDocumentNameWithIcon actions={actions} row={row.original} />,
		},
		{
			id: "statementDate",
			accessorFn: ({ wipExtraction }) =>
				wipExtraction?.statementDate ? DateTime.fromISO(wipExtraction.statementDate) : undefined,
			header: "Statement Date",
			size: 130,
			sortingFn: "datetime",
			enableSorting: true,
			cell: ({ row }) => {
				const statementDate = row.original.wipExtraction?.statementDate;
				if (!statementDate) {
					return <span className="text-tertiary">—</span>;
				}
				const dt = DateTime.fromISO(statementDate);
				return (
					<span className="text-secondary text-xs xl:text-sm">
						{dt.isValid ? dt.toFormat("MM/dd/yyyy") : "—"}
					</span>
				);
			},
		},
		{
			id: "extractionConfidence",
			accessorFn: ({ wipExtraction }) =>
				wipExtraction?.extractionConfidence
					? WIP_CONFIDENCE_LABELS[wipExtraction.extractionConfidence]
					: "",
			header: "Confidence",
			size: 110,
			headerFilter: { type: "checkbox" },
			cell: ({ row }) => {
				const confidence = row.original.wipExtraction?.extractionConfidence;
				if (!confidence) {
					return <span className="text-tertiary">—</span>;
				}
				return <span className="text-secondary text-sm">{WIP_CONFIDENCE_LABELS[confidence]}</span>;
			},
		},
		{
			id: "uploadedAt",
			accessorKey: "createdAtMs",
			header: "Uploaded",
			size: 110,
			sortDescFirst: true,
			enableSorting: true,
			cell: ({ row }) => (
				<span className="text-secondary text-xs xl:text-sm">
					{DateTime.fromMillis(row.original.createdAtMs).toFormat("MM/dd/yyyy")}
				</span>
			),
		},
		{
			id: "extractionStatus",
			accessorFn: ({ wipExtraction }) =>
				wipExtraction?.status ? ExtractionStatus.displayName(wipExtraction.status) : "",
			header: "Status",
			size: 100,
			headerFilter: { type: "checkbox" },
			cellStyle: { centerItems: true },
			cell: ({ row }) => (
				<ExtractionStatusIcon
					status={row.original.wipExtraction?.status}
					errorType={row.original.wipExtraction?.errorType}
					errorMessage={row.original.wipExtraction?.errorMessage}
					extractionTriggeredAt={
						row.original.wipExtraction?.extractionTriggeredAt
							? DateTime.fromISO(row.original.wipExtraction.extractionTriggeredAt)
							: undefined
					}
					extractionCompletedAt={
						row.original.wipExtraction?.extractionCompletedAt
							? DateTime.fromISO(row.original.wipExtraction.extractionCompletedAt)
							: undefined
					}
				/>
			),
		},
	];
}
